import useTranslator from "@pma/hooks/use-translator"
import { cn } from "@pma/utils/helpers"

interface BillStatusBadgeProps {
  status: string
  isApproved?: boolean
  isSigned?: boolean
  isDeclined?: boolean
  isReturnedToContractor?: boolean
  className?: string
  size?: "sm" | "md"
}

const statusConfig: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  new: { label: "new", bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  pending: { label: "pending approval", bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  approved: { label: "approved", bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  for_sign: { label: "awaiting signature", bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-500" },
  signed: { label: "signed", bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  paid: { label: "paid", bg: "bg-teal-50", text: "text-teal-700", dot: "bg-teal-500" },
  declined: { label: "declined", bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  returned: { label: "returned for review", bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500" },
}

export function BillStatusBadge({ status, isApproved, isSigned, isDeclined, isReturnedToContractor, className, size = "sm" }: BillStatusBadgeProps) {
  const { translate } = useTranslator()

  // Derive effective status from boolean flags if raw status not definitive
  let effectiveStatus = status
  if (isReturnedToContractor) effectiveStatus = "returned"
  else if (isDeclined) effectiveStatus = "declined"
  else if (isSigned && isApproved) effectiveStatus = "signed"
  else if (isApproved && !isSigned) effectiveStatus = "for_sign"
  else if (status === "pending") effectiveStatus = "pending"
  else if (status === "new") effectiveStatus = "new"

  const config = statusConfig[effectiveStatus] || statusConfig.new

  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded-full font-medium",
      size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm",
      config.bg, config.text,
      className
    )}>
      <span className={cn("h-1.5 w-1.5 rounded-full", config.dot)} />
      {translate(config.label)}
    </span>
  )
}
